import { Alert, Checkbox, Modal, notification, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload';
import { UploadOutlined } from '@ant-design/icons';
import { useState } from 'react';
import APIClient from '../api/api';
import { Button } from './ui';

interface Props {
  onClose: () => void;
  onUploaded: () => void;
}

export function PricingUploadModal({ onClose, onUploaded }: Props) {
  const [providers, setProviders] = useState<string[]>(['afternic', 'sedo']);
  const [file, setFile] = useState<UploadFile>();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  async function upload() {
    if (!file?.originFileObj || providers.length === 0 || uploading) return;
    setUploading(true);
    setError('');
    const data = new FormData();
    data.append('uploadPricing', file.originFileObj);
    data.append('providers', JSON.stringify(providers));
    try {
      const response = await APIClient.instance.post('/v1/sales/uploadPricing', data);
      notification.success({
        message: 'Pricing upload queued',
        description: `${(response.data.processed || 0).toLocaleString()} records queued for ${providers.map(p => p === 'sedo' ? 'Sedo' : 'Afternic').join(' and ')}.`,
      });
      onUploaded();
    } catch (e: any) {
      setError(typeof e.response?.data?.error === 'string'
        ? e.response.data.error
        : 'Pricing file failed to upload. Check the CSV and try again.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <Modal
      open
      title="Upload Pricing"
      onCancel={onClose}
      closable={!uploading}
      maskClosable={!uploading}
      keyboard={!uploading}
      width={560}
      footer={[
        <Button key="cancel" disabled={uploading} onClick={onClose}>Cancel</Button>,
        <Button key="upload" variant="primary" loading={uploading} disabled={!file || providers.length === 0} onClick={upload}>
          Upload Pricing
        </Button>,
      ]}
    >
      <div className="flex flex-col gap-4 py-3">
        <fieldset disabled={uploading} className="m-0 border-0 p-0">
          <legend className="text-text font-medium mb-2">Update listings on</legend>
          <Checkbox.Group
            aria-label="Listing providers"
            options={[{ label: 'Afternic', value: 'afternic' }, { label: 'Sedo', value: 'sedo' }]}
            value={providers}
            disabled={uploading}
            onChange={values => setProviders(values as string[])}
          />
          {providers.length === 0 && <p role="alert" className="text-danger mt-2">Select at least one provider.</p>}
        </fieldset>
        <p className="text-text-2 m-0">
          Pricing is saved to your portfolio and sent to the selected providers.
        </p>
        <div>
          <p className="text-text-2 mb-2">
            Use a comma-separated CSV with Domain (or Domain Name) and at least one of:
            Asking Price, Minimum Offer, Floor Price.
          </p>
          <Upload
            accept=".csv,text/csv"
            maxCount={1}
            disabled={uploading}
            fileList={file ? [file] : []}
            beforeUpload={() => false}
            onChange={info => { setFile(info.fileList[0]); setError(''); }}
            onRemove={() => { setFile(undefined); setError(''); }}
          >
            <Button disabled={uploading} iconLeft={<UploadOutlined />}>Choose CSV</Button>
          </Upload>
        </div>
        {error && <Alert type="error" showIcon message={error} />}
      </div>
    </Modal>
  );
}
